/**
 * NaamDarbar — Admin Layout
 *
 * Sidebar navigation wrapping all admin pages.
 * Redirects to /admin (login) if the admin key is not set.
 */

import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { Suspense, useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { api } from '../../lib/api';

const NAV = [
  { label: 'Dashboard', href: '/admin/dashboard', icon: '📊' },
  { label: 'Ragis', href: '/admin/artists', icon: '🎤' },
  { label: 'Ragi Claims', href: '/admin/claims', icon: '📋' },
  { label: 'Gurudwaras', href: '/admin/gurudwaras', icon: '🕌' },
  { label: 'Samagams', href: '/admin/events', icon: '📅' },
  { label: 'Ragi Merge', href: '/admin/ragi-merge', icon: '🔀' },
  { label: 'Duplicates', href: '/admin/duplicates', icon: '👥' },
  { label: 'Gurudwara Dupes', href: '/admin/gurudwara-duplicates', icon: '🏛️' },
  { label: 'Event Dupes', href: '/admin/event-duplicates', icon: '📅' },
  { label: 'Ingestion', href: '/admin/ingest', icon: '📥' },
  { label: 'Amrit Sanchar', href: '/admin/amrit-sanchar', icon: '💧' },
  { label: 'Agan Bhet Sewa', href: '/admin/birdh-sewa-leads', icon: '🔥' },
  { label: 'Cronjobs', href: '/admin/cronjobs', icon: '⚙️' },
  { label: 'Delete User', href: '/admin/users', icon: '🗑️' },
];

function SidebarContent({ onNavClick }: { onNavClick?: () => void }) {
  const { logout, adminHeaders } = useAdmin();
  const navigate = useNavigate();

  const handleLogout = async () => {
    // Best-effort server-side audit log; don't block local logout on failure.
    try {
      await api.post('/admin/auth/logout', null, { headers: adminHeaders() });
    } catch {
      /* ignore — token may already be expired */
    }
    logout();
    navigate('/admin');
  };
  return (
    <>
      <div className="p-4 border-b border-line dark:border-gray-800">
        <span className="flex items-baseline gap-0.5">
          <span className="font-logo-darbar text-lg tracking-wide text-tx-primary dark:text-slate-100">
            Kirtan
          </span>
          <span className="font-logo-darbar text-lg tracking-wide text-kesri-500">
            Darbar
          </span>
          <span className="text-sm font-medium text-tx-muted dark:text-gray-400 ml-1">
            Admin
          </span>
        </span>
      </div>

      <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto">
        {NAV.map(({ label, href, icon }) => (
          <NavLink
            key={href}
            to={href}
            onClick={onNavClick}
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                  : 'text-tx-secondary dark:text-gray-400 hover:bg-elevated dark:hover:bg-gray-800'
              }`
            }
          >
            <span>{icon}</span>
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-line dark:border-gray-800">
        <button
          onClick={handleLogout}
          className="w-full text-sm text-tx-muted hover:text-red-600 dark:hover:text-red-400 transition-colors text-left"
        >
          Logout
        </button>
      </div>
    </>
  );
}

export default function AdminLayout() {
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-elevated dark:bg-gray-950">

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-56 bg-card dark:bg-gray-900 border-r border-line dark:border-gray-800 flex-col flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden flex items-center justify-between px-4 py-3 bg-card dark:bg-gray-900 border-b border-line dark:border-gray-800">
        <span className="flex items-baseline gap-0.5">
          <span className="font-logo-darbar text-lg tracking-wide text-tx-primary dark:text-slate-100">Kirtan</span>
          <span className="font-logo-darbar text-lg tracking-wide text-kesri-500">Darbar</span>
          <span className="text-sm font-medium text-tx-muted dark:text-gray-400 ml-1">Admin</span>
        </span>
        <button
          onClick={() => setMobileOpen(o => !o)}
          className="p-2 rounded-lg text-tx-secondary dark:text-gray-400 hover:bg-elevated dark:hover:bg-gray-800 transition-colors"
          aria-label="Toggle navigation"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </header>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-card dark:bg-gray-900 flex flex-col md:hidden transform transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent onNavClick={() => setMobileOpen(false)} />
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto p-4 md:p-6">
        <Suspense
          fallback={
            <div className="flex items-center justify-center min-h-[40vh]">
              <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
